/**
 * @module passenger.service
 */
import { DialVerseApi } from '../app/api'
import AppConfig from '../config'

const PassengerService = DialVerseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Basic CRUD operations
    getPassengers: builder.mutation({
      query: () => ({
        url: AppConfig.getUrl('/passengers'),
        method: 'GET',
      }),
    }),
    getPassenger: builder.mutation({
      query: (passengerId) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}`),
        method: 'GET',
      }),
    }),
    getPassengerByEmail: builder.mutation({
      query: (email) => ({
        url: AppConfig.getUrl(`/passengers/email/${email}`),
        method: 'GET',
      }),
    }),
    createPassenger: builder.mutation({
      query: (passenger) => ({
        url: AppConfig.getUrl('/passengers'),
        method: 'POST',
        body: passenger,
      }),
    }),
    updatePassenger: builder.mutation({
      query: ({ passengerId, ...updates }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}`),
        method: 'PUT',
        body: updates,
      }),
    }),
    deletePassenger: builder.mutation({
      query: (passengerId) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}`),
        method: 'DELETE',
      }),
    }),

    // Status and profile updates
    updatePassengerStatus: builder.mutation({
      query: ({ passengerId, status }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/status`),
        method: 'PATCH',
        body: { status },
      }),
    }),
    updatePassport: builder.mutation({
      query: ({ passengerId, passport }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/passport`),
        method: 'PATCH',
        body: { passport },
      }),
    }),
    updateContactInfo: builder.mutation({
      query: ({ passengerId, contactInfo }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/contact`),
        method: 'PATCH',
        body: { contactInfo },
      }),
    }),

    // Loyalty program operations
    updateLoyaltyProgram: builder.mutation({
      query: ({ passengerId, loyaltyProgram }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/loyalty`),
        method: 'PATCH',
        body: { loyaltyProgram },
      }),
    }),
    addLoyaltyPoints: builder.mutation({
      query: ({ passengerId, points }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/loyalty/points`),
        method: 'POST',
        body: { points },
      }),
    }),
    redeemLoyaltyPoints: builder.mutation({
      query: ({ passengerId, points }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/loyalty/redeem`),
        method: 'POST',
        body: { points },
      }),
    }),
    upgradeLoyaltyTier: builder.mutation({
      query: ({ passengerId, tier }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/loyalty/tier`),
        method: 'PATCH',
        body: { tier },
      }),
    }),

    // Booking operations
    addBooking: builder.mutation({
      query: ({ passengerId, booking }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/bookings`),
        method: 'POST',
        body: { booking },
      }),
    }),
    updateBooking: builder.mutation({
      query: ({ passengerId, bookingReference, updates }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/bookings/${bookingReference}`),
        method: 'PATCH',
        body: updates,
      }),
    }),

    // Payment method operations
    addPaymentMethod: builder.mutation({
      query: ({ passengerId, paymentMethod }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/payment-methods`),
        method: 'POST',
        body: { paymentMethod },
      }),
    }),
    removePaymentMethod: builder.mutation({
      query: ({ passengerId, lastFourDigits }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/payment-methods/${lastFourDigits}`),
        method: 'DELETE',
      }),
    }),
    setDefaultPaymentMethod: builder.mutation({
      query: ({ passengerId, lastFourDigits }) => ({
        url: AppConfig.getUrl(
          `/passengers/${passengerId}/payment-methods/${lastFourDigits}/default`,
        ),
        method: 'PATCH',
      }),
    }),

    // Travel document operations
    addTravelDocument: builder.mutation({
      query: ({ passengerId, travelDocument }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/travel-documents`),
        method: 'POST',
        body: { travelDocument },
      }),
    }),
    removeTravelDocument: builder.mutation({
      query: ({ passengerId, documentNumber }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/travel-documents/${documentNumber}`),
        method: 'DELETE',
      }),
    }),

    // Special requirements operations
    updateSpecialRequirements: builder.mutation({
      query: ({ passengerId, specialRequirements }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/special-requirements`),
        method: 'PATCH',
        body: { specialRequirements },
      }),
    }),
    addSpecialAssistance: builder.mutation({
      query: ({ passengerId, assistance }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/special-assistance`),
        method: 'POST',
        body: { assistance },
      }),
    }),
    removeSpecialAssistance: builder.mutation({
      query: ({ passengerId, assistance }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/special-assistance/${assistance}`),
        method: 'DELETE',
      }),
    }),

    // Medical and dietary operations
    addMedicalCondition: builder.mutation({
      query: ({ passengerId, condition }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/medical-conditions`),
        method: 'POST',
        body: { condition },
      }),
    }),
    removeMedicalCondition: builder.mutation({
      query: ({ passengerId, condition }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/medical-conditions/${condition}`),
        method: 'DELETE',
      }),
    }),
    addDietaryRestriction: builder.mutation({
      query: ({ passengerId, type }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/dietary-restrictions`),
        method: 'POST',
        body: { type },
      }),
    }),
    removeDietaryRestriction: builder.mutation({
      query: ({ passengerId, type }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/dietary-restrictions/${type}`),
        method: 'DELETE',
      }),
    }),

    // Mobility assistance
    updateMobilityAssistance: builder.mutation({
      query: ({ passengerId, mobilityAssistance }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/mobility-assistance`),
        method: 'PATCH',
        body: { mobilityAssistance },
      }),
    }),

    // Communication preferences
    updateCommunicationPreferences: builder.mutation({
      query: ({ passengerId, communicationPreferences }) => ({
        url: AppConfig.getUrl(`/passengers/${passengerId}/communication-preferences`),
        method: 'PATCH',
        body: { communicationPreferences },
      }),
    }),

    // Airline-related operations
    getPassengersByAirline: builder.mutation({
      query: (airlineId) => ({
        url: AppConfig.getUrl(`/passengers/airline/${airlineId}`),
        method: 'GET',
      }),
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetPassengersMutation,
  useGetPassengerMutation,
  useGetPassengerByEmailMutation,
  useCreatePassengerMutation,
  useUpdatePassengerMutation,
  useDeletePassengerMutation,
  useUpdatePassengerStatusMutation,
  useUpdatePassportMutation,
  useUpdateContactInfoMutation,
  useUpdateLoyaltyProgramMutation,
  useAddLoyaltyPointsMutation,
  useRedeemLoyaltyPointsMutation,
  useUpgradeLoyaltyTierMutation,
  useAddBookingMutation,
  useUpdateBookingMutation,
  useAddPaymentMethodMutation,
  useRemovePaymentMethodMutation,
  useSetDefaultPaymentMethodMutation,
  useAddTravelDocumentMutation,
  useRemoveTravelDocumentMutation,
  useUpdateSpecialRequirementsMutation,
  useAddSpecialAssistanceMutation,
  useRemoveSpecialAssistanceMutation,
  useAddMedicalConditionMutation,
  useRemoveMedicalConditionMutation,
  useAddDietaryRestrictionMutation,
  useRemoveDietaryRestrictionMutation,
  useUpdateMobilityAssistanceMutation,
  useUpdateCommunicationPreferencesMutation,
  useGetPassengersByAirlineMutation,
} = PassengerService
